import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { iconMap } from '../lib/iconRegistry';

const hasDescendantPath = (items, path) => {
  if (!items) return false;
  for (const child of items) {
    if (child.path === path) return true;
    if (child.children && hasDescendantPath(child.children, path)) return true;
  }
  return false;
};

const NestedMenu = ({ item, user, isAdmin, level = 0, searchActive = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const location = useLocation();

  // O filtro de permissões já foi feito em Navigation.js
  // Não precisamos verificar novamente aqui

  // Suporta tanto ícone como string (do JSON) quanto como componente React (legado)
  const Icon = typeof item.icon === 'string' ? iconMap[item.icon] : item.icon;
  const hasChildren = item.children && item.children.length > 0;
  const isActive = item.path && location.pathname === item.path;

  // Auto-expandir se algum descendente corresponder à rota atual
  useEffect(() => {
    if (hasChildren && hasDescendantPath(item.children, location.pathname)) {
      setIsExpanded(true);
    }
  }, [location.pathname]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const renderContent = () => {
    const iconElement = Icon && (
      <Icon className="h-4 w-4 mr-3 transition-transform group-hover:scale-110" />
    );

    const labelElement = (
      <span className="truncate">{item.label}</span>
    );

    const chevronElement = hasChildren && (
      <div className="ml-auto">
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 transition-transform" />
        ) : (
          <ChevronRight className="h-4 w-4 transition-transform" />
        )}
      </div>
    );

    const baseClasses = `group flex items-center w-full px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200`;
    
    const stateClasses = isActive
      ? 'bg-white/30 text-white shadow-lg backdrop-blur-sm border-l-2 border-orange-400'
      : 'text-white/70 hover:bg-white/10 hover:text-white hover:shadow-md';

    if (hasChildren) {
      // Render as button for expandable items
      return (
        <button
          onClick={toggleExpanded}
          className={`${baseClasses} ${stateClasses}`}
          title={item.label}
        >
          {iconElement}
          {labelElement}
          {chevronElement}
        </button>
      );
    } else if (item.path) {
      // Render as link for navigation items
      return (
        <Link
          to={item.path}
          className={`${baseClasses} ${stateClasses}`}
          title={item.label}
        >
          {iconElement}
          {labelElement}
          {isActive && (
            <div className="ml-auto w-2 h-2 bg-green-400 rounded-full"></div>
          )}
        </Link>
      );
    } else {
      // Render as static item
      return (
        <div className={`${baseClasses} ${stateClasses}`} title={item.label}>
          {iconElement}
          {labelElement}
        </div>
      );
    }
  };

  return (
    <div>
      {/* Main item */}
      {renderContent()}
      
      {/* Children */}
      {hasChildren && (isExpanded || searchActive) && (
        <div className={`ml-${level === 0 ? '4' : '6'} mt-1 space-y-1`}>
          {item.children.map((child) => (
            <NestedMenu
              key={child.path || child.label}
              item={child}
              user={user}
              isAdmin={isAdmin}
              level={level + 1}
              searchActive={searchActive}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default NestedMenu;